import React, {
  useCallback, useEffect, useRef, useState,
} from "react";
import {
  ButtonColour, Typography, TypographyVariants, ButtonSize,
} from "@trussle/external-components";
import { renderRichText } from "gatsby-source-contentful/rich-text";
import { addTracking, CallToActions } from "../../../CallToActions";
import { ReplaceQueryParams } from "../../../ReplaceQueryParams";
import { generateId } from "../../../../helpers/generateId";
import { ISplashPanelProps } from "../OriginalSplashPanel";
import BackgroundIcon from "./images/splash-panel-sunset-icon.svg";

import "./styles.scss";

export const SunsetSplashPanel: React.FunctionComponent<ISplashPanelProps> = ({
  title,
  headingType,
  mainContent,
  ctas,
  headerAnchor,
  alignment = "left",
  preset,
  betterV2,
}) => {
  const [height, setHeight] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  const selectedPreset = preset ?? "default";

  const onResize = useCallback(() => {
    if (ref.current) {
      setHeight(ref.current.clientHeight);
    }
  }, []);
  useEffect(() => {
    window.addEventListener("resize", onResize);
    onResize();
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const sunsetV1 = (
    <div id={headerAnchor} className={`sunsetSplashPanel sunsetSplashPanel--preset sunsetSplashPanel--preset-${selectedPreset}`}>
      <div
        className="sunsetSplashPanel__main"
        role="region"
        aria-label="Splash panel"
      >
        {title && (
        <Typography
          classes="sunsetSplashPanel__title"
          variant={(headingType as TypographyVariants) || "h2"}
          {...(title && {
            id: generateId(title),
          })}
        >
          <ReplaceQueryParams>{title}</ReplaceQueryParams>
        </Typography>
        )}
        <div className="sunsetStyledRichTextContainer">
          {mainContent && renderRichText(mainContent)}
        </div>
        {ctas && (
          <div className={`sunsetSplashPanel--ctas-${selectedPreset} sunsetSplashPanel--ctas-${alignment}`}>
            <CallToActions
              alignment={alignment}
              primaryCtaColour={ButtonColour.Secondary}
              secondaryCtaColour={ButtonColour.Primary}
              ctas={addTracking("Splash", ...ctas)}
              size={ButtonSize.Large}
            />
          </div>
        )}
      </div>
      <div className="geometricContainer">
        {["circleOne", "circleTwo"].map((circle) => (
          <div
            key={circle}
            className={`circle ${circle} ${circle}-${selectedPreset}`}
            style={{
              height: height / 2,
              width: height,
              borderRadius: `${height / 2}px ${height / 2}px 0 0`,
            }}
          />
        ))}
      </div>
    </div>
  );

  const sunsetV2 = (
    <div className="sunsetSplashPanelWrapper">
      <div
        className="sunsetSplashPanelContent"
        role="region"
        aria-label="Splash panel"
      >
        {title && (
          <Typography
            classes="sunsetSplashPanelContent__title"
            variant={(headingType as TypographyVariants) || "h2"}
            {...(title && {
              id: generateId(title),
            })}
          >
            <ReplaceQueryParams>{title}</ReplaceQueryParams>
          </Typography>
        )}

        <div className="sunsetSplashPanelContent__body">
          {mainContent && renderRichText(mainContent)}
        </div>

        {ctas && (
          <div className="sunsetSplashPanelContent__cta">
            <CallToActions
              alignment={alignment}
              primaryCtaColour={ButtonColour.DarkGreen}
              secondaryCtaColour={ButtonColour.DarkGreen}
              ctas={addTracking("Splash", ...ctas)}
              size={ButtonSize.Large}
            />
          </div>
        )}
      </div>
      <img
        src={BackgroundIcon}
        alt="Background Icon"
        className="sunsetSplashPanelWrapper__img"
      />
    </div>
  );

  return (
    <div className={`sunsetSplashPanelContainer${betterV2 ? "-v2" : ""}`} ref={ref}>
      {betterV2 ? sunsetV2 : sunsetV1}
    </div>
  );
};
